import { IFilterModel } from "@directives/_resourceFilters/IFilterModel";
import { IAutocompleteMatch } from "@directives/_forms/autocomplete/autocomplete.component";
import { IVeriMapComponent, MapPopupType } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";
import { IVeriTableHeader, IVeriTableRow } from "@directives/_veri/veriTable/veriTable.component";
import { DateRange } from "@enums/DateRange.enum";
import { ReasonCode } from "@enums/ReasonCode";
import { IDateFilter } from "@interfaces/filters/IDateFilter";
import { IGroupFilter } from "@interfaces/filters/IGroupFilter";
import { IDriver } from "@interfaces/IDriver";
import { IGoogleMapsMarker } from "@interfaces/IGoogleMapsMarkerOptions";
import { IJourney } from "@interfaces/IJourney";
import { IPlot } from "@interfaces/IPlot";
import { IVehicle } from "@interfaces/IVehicle";
import { ColourService } from "@services/colour.service";
import { DriverService } from "@services/driver.service";
import { GoogleMarkerService, MarkerAnchorPosition } from "@services/googleMarker.service";
import { IVehicleJourneysMap as IVehicleJourneysMap, JourneyService } from "@services/journey.service";
import { MapService } from "@services/map.service";
import { PlotService } from "@services/plot.service";
import { VehicleService } from "@services/vehicle.service";
import { VeriDate } from "@services/veriDate.service";
import * as angular from "angular";
import * as h3 from "h3-js";
import * as moment from "moment";
import { IMarkerOptions, IPolygon, IPolyline } from "@typings/google.maps.angular";
import { IVehicleData } from "../../fleetMaintenance/IVehicleData";
import { VehicleDataService } from "../../fleetMaintenance/vehicleData.service";
import { CoordsModel } from "../../livemap/model/CoordsModel";
import { h3Service } from "../h3.service";
import { ITimeRangeActivityBar } from "./components/fleetHistoryTimeRangeSelector/fleetHistoryTimeRangeSelector.component";
import "./fleetHistory.component.scss";

interface IFleetHistoryFilters {
    dateFilter?: IDateFilter;
    groupFilter?: IGroupFilter;
    defaultDateRange?: number;
    timeFilterFrom?: IFleetHistoryTimeFilter;
    timeFilterTo?: IFleetHistoryTimeFilter;
}

interface IFleetHistoryTimeFilter {
    hour?: number;
    minute?: number;
}

export interface IFleetHistoryPlotGroup {
    plots: IPlot[];
    inTimeFilter: boolean;
}

interface IComponentBindings {
    resourceFilter: IFilterModel;
}

interface IFleetHistoryJourney extends IJourney {
    vehicle?: IFleetHistoryVehicle;
}

interface IFleetHistoryVehicle extends IVehicle {
    journeyColor?: string;
}

class ComponentController implements angular.IComponentController, IComponentBindings {

    // bindings
    resourceFilter: IFilterModel;

    // configuration
    maxVeriMapZoomLevel: number = 17;
    vehicleMarkerMinZoomLevel: number = 10;
    readonly MAX_PLOT_PROMISE_COUNT: number = 40;

    // raw data
    isLoadingData: boolean = false;
    journeys: IFleetHistoryJourney[] = [];
    drivers: IDriver[] = [];
    vehicles: IFleetHistoryVehicle[] = [];
    vehicleData: IVehicleData[] = [];

    //
    selectedJourney: IFleetHistoryJourney;
    totalJourneysVisibleInBounds: number; // refactor?
    totalDriversVisibleInBounds: number; // refactor?
    totalVehiclesVisibleInBounds: number; // refactor?
    journeyList: IFleetHistoryJourney[] = []; // rename or remove?
    journeyMap: IFleetHistoryJourney[] = []; //
    journeyPolylines: IPolyline[] = [];
    vehicleJourneysMaps: IVehicleJourneysMap[] = [];
    mapHoverJourney: IFleetHistoryJourney;

    timeRangeActivityBars: ITimeRangeActivityBar[] = [];
    creatingClosestPlotMarkers: boolean = false;
    filteredStartMinuteOfDay: number; // refactor usage to pass moment to scrubber instead of minutes?
    filteredEndMinuteOfDay: number; // refactor usage to pass moment to scrubber instead of minutes?

    // map
    veriMap: IVeriMapComponent;
    mouseLatLngs: google.maps.LatLng;
    mapZoomLevel: number = -1;
    mapCenterFromSelectedJourney: CoordsModel;
    mouseMoveJourneyPlotMarkers: IGoogleMapsMarker[] = [];

    selectedJourneyMapMarkerStart: IGoogleMapsMarker;
    selectedJourneyMapMarkerEnd: IGoogleMapsMarker;

    hoveredJourneyMapMarkerStart: IGoogleMapsMarker;
    hoveredJourneyMapMarkerEnd: IGoogleMapsMarker;
    hoveredJourneyPlotMarkers: IGoogleMapsMarker[] = [];

    // filters
    filters: IFleetHistoryFilters = {
        defaultDateRange: DateRange.Yesterday,
        dateFilter: null,
        groupFilter: null,
        timeFilterFrom: {
            hour: null,
            minute: null
        },
        timeFilterTo: {
            hour: null,
            minute: null
        }
    };

    // marker options
    vehiclesDisplay: IVehicle[] = [];
    vehicleMarkerOptions: IMarkerOptions<IVehicle> = {
        events: {},
        control: {},
        type: "cluster",
        typeOptions: {
            imagePath: "https://rawgit.com/googlemaps/js-marker-clusterer/gh-pages/images/m",
            maxZoom: 13,
            gridSize: 40 // default 60
        },
        typeEvents: {}
    };

    heatMapPolygons: IPolygon[] = [];
    inHeatmapMode: boolean = false;

    exportData: IVeriTableRow<any>[] = [];
    exportDataHeaders: IVeriTableHeader[] = [
        { title: "Journey" },
        { title: "Time" },
        { title: "Speed" },
        { title: "Direction" },
        { title: "Reason" },
        { title: "Odometer" },
        { title: "Location" }
    ];

    static $inject = ["$q", "$timeout", "$scope", "plotService", "mapService", "journeyService", "vehicleService", "vehicleDataService", "driverService", "h3Service", "googleMarkerService", "veriDate", "colourService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $timeout: angular.ITimeoutService,
        private readonly $scope: angular.IScope,
        private readonly plotService: PlotService,
        private readonly mapService: MapService,
        private readonly journeyService: JourneyService,
        private readonly vehicleService: VehicleService,
        private readonly vehicleDataService: VehicleDataService,
        private readonly driverService: DriverService,
        private readonly h3Service: h3Service,
        private readonly googleMarkerService: GoogleMarkerService,
        private readonly veriDate: VeriDate,
        private readonly colourService: ColourService) { }

    $onInit(): void {
        this.resetFilters();
        this.clearAllData();
    }

    $onDestroy() {
        this.clearAllData();
    }

    onSearchSelectionUpdated(match: IAutocompleteMatch) {
        if (!this.veriMap || !match?.location?.lat || !match?.location?.lng) {
            return;
        }

        this.veriMap.smoothPanAndZoomTo(new google.maps.LatLng(match.location.lat, match.location.lng), 14);
    }

    onSelectedScrubberTimeRangeChanged(startMoment: moment.Moment, endMoment: moment.Moment): void {
        this.onTimeFilterFromChanged(startMoment.hour(), startMoment.minute());
        this.onTimeFilterToChanged(endMoment.hour(), endMoment.minute());
    }

    onTimeFilterFromChanged(hour: number, minute: number): void {
        this.filters.timeFilterFrom.hour = hour;
        this.filters.timeFilterFrom.minute = minute;

        this.updateStartMinuteOfDay(this.filters.timeFilterFrom);

        this.refreshMapWithOptionalFit(false);
        this.createClosestPlotmarkersForJourneys(this.journeyMap.filter(journey => journey.visibleInBounds));
    }

    onTimeFilterToChanged(hour: number, minute: number): void {
        this.filters.timeFilterTo.hour = hour;
        this.filters.timeFilterTo.minute = minute;

        this.updateEndMinuteOfDay(this.filters.timeFilterTo);

        this.refreshMapWithOptionalFit(false);
        this.createClosestPlotmarkersForJourneys(this.journeyMap.filter(journey => journey.visibleInBounds));
    }

    onVeriMapLoaded(veriMap: IVeriMapComponent): void {
        this.veriMap = veriMap;

        this.refreshMapWithOptionalFit(true);
    }

    onVeriMapIdle(veriMap: IVeriMapComponent): void {
        this.mapZoomLevel = veriMap.getMap().getZoom();
        if (this.mapZoomLevel > this.maxVeriMapZoomLevel) {
            veriMap.getMap().setZoom(this.maxVeriMapZoomLevel);
        }

        this.refreshMapWithOptionalFit(false);
    }

    onVehicleMouseOver(vehicle: IVehicle): void {
        if (!vehicle || !vehicle?.Plot?.Latitude || !vehicle?.Plot?.Longitude) {
            return;
        }

        const journey: IFleetHistoryJourney = this.getJourneyFromPlotId(vehicle.Plot.Id);
        const latLngs: google.maps.LatLng = new google.maps.LatLng(vehicle.Plot.Latitude, vehicle.Plot.Longitude);

        if (journey && latLngs) {
            this.mapHoverJourney = journey;
            this.veriMap.showPopup(MapPopupType.Custom, latLngs, event);
        }
    }

    onVehicleMouseOut(): void {
        this.veriMap.hidePopup(MapPopupType.Custom, false);
    }

    onDateFilterChanged(filter: IDateFilter): void {
        this.filters.dateFilter = filter;

        this.refreshFleetHistoryData();
    }

    onGroupFilterChanged(filter: IGroupFilter): void {
        this.filters.groupFilter = filter;

        this.refreshFleetHistoryData();
    }

    onTimelineScrubberMouseMove(time: moment.Moment): void {
        this.createClosestPlotmarkersForJourneys(this.journeyMap.filter(journey => journey.visibleInBounds), time);
    }

    setInHeatmapMode(value: boolean): void {
        this.inHeatmapMode = value;

        this.refreshMapWithOptionalFit(false);
    }

    // -----

    private clearAllData(): void {
        this.journeys = [];
        this.drivers = [];
        this.vehicles = [];
        this.vehicleData = [];
        this.journeyList = [];
        this.journeyMap = [];
        this.journeyPolylines = [];
        this.vehicleJourneysMaps = [];
        this.timeRangeActivityBars = [];
        this.mouseMoveJourneyPlotMarkers = [];
        this.hoveredJourneyPlotMarkers = [];
        this.heatMapPolygons = [];
    }

    private getJourneyFromPlotId(plotId: number): IFleetHistoryJourney {
        return this.journeyMap.find(journey => journey.plots?.find(plot => plot.Id === plotId));
    }

    private selectJourney(journey: IFleetHistoryJourney): void {
        this.selectedJourney = journey;

        this.createSelectedJourneyMarkers(this.selectedJourney);
        this.refreshMapWithOptionalFit(false);
        this.createClosestPlotmarkersForJourneys(
            this.journeyMap.filter(journey => journey.visibleInBounds),
            this.selectedJourney.vehicle.Plot.UtcMoment);

        this.$timeout(() => {
            this.$scope.$apply();
        }, 100);
    }

    private clearSelectedJourney(): void {
        this.clearSelectedJourneyMarkers();

        this.selectedJourney = null;
        this.refreshMapWithOptionalFit(false);
    }

    private clearSelectedJourneyMarkers(): void {
        this.selectedJourneyMapMarkerStart = null;
        this.selectedJourneyMapMarkerEnd = null;
        this.mouseMoveJourneyPlotMarkers = [];
    }

    private clearHoveredJourneyMarkers(): void {
        this.hoveredJourneyMapMarkerStart = null;
        this.hoveredJourneyMapMarkerEnd = null;
    }

    private getPlotsForJourneyAndVehicle(journey: IFleetHistoryJourney): angular.IPromise<any> {
        if (!journey?.vehicle?.Id || !journey?.StartUtc || !journey?.EndUtc || this.journeyHasPlots(journey)) {
            return this.$q.resolve();
        }

        return this.plotService.getPlotsForDateRange(journey.vehicle.Id, this.veriDate.createMoment(journey.StartUtc), this.veriDate.createMoment(journey.EndUtc)).then((plots) => {
            plots.forEach(plot => {
                plot.journeyId = journey.Id;
            });

            journey.plots = plots;
        });
    }

    private refreshFleetHistoryData(): void {
        if (this.isLoadingData) {
            return;
        }

        this.isLoadingData = true;
        this.clearAllData();

        this.getFleetHistoryData().then(() => {
            this.prepareDefaultView();

            this.isLoadingData = false;
        });
    }

    private getFleetHistoryData(): angular.IPromise<any> {
        const dateFilterStart = (this.filters.dateFilter && this.filters.dateFilter.start)
            ? this.veriDate.createMoment(this.filters.dateFilter.start)
            : moment().startOf("d");

        const dateFilterEnd = (this.filters.dateFilter && this.filters.dateFilter.end)
            ? this.veriDate.createMoment(this.filters.dateFilter.end)
            : moment().endOf("d");

        const driversPromise = this.driverService.getDrivers();
        const vehiclesPromise = this.vehicleService.getVehicles({ includePeripheralMappings: true });
        const vehicleDataPromise = this.vehicleDataService.getVehicleData();
        const journeysPromise = this.journeyService.getAllJourneys(dateFilterStart.toDate(), dateFilterEnd.toDate());

        return this.$q.all([driversPromise, vehiclesPromise, vehicleDataPromise, journeysPromise]).then(([drivers, vehicles, vehicleData, journeys]) => {
            this.drivers = drivers;
            this.vehicles = vehicles;
            this.vehicleData = vehicleData;
            this.journeys = journeys;

            this.postProcessFleetHistoryData();
        });
    }

    private postProcessFleetHistoryData(): void {
        this.filterDataByGroupFilter();

        this.journeys.forEach(journey => {
            if (journey.LinePoints) {
                journey.LinePoints = this.mapService.simplifyRoute(journey.LinePoints, 0.001, true);
            }

            journey.driver = this.driverService.findDriver(this.drivers, journey.DriverId);

            this.journeyService.updateTotalElapsedMinutesForJourney(journey);
            this.journeyService.updateStartMinuteOfDayForJourney(journey);
            this.journeyService.updateEndMinuteOfDayForJourney(journey);
        });
    }

    private filterDataByGroupFilter(): void {
        const filterByDriverIds = this.filters?.groupFilter?.group?.DriverIds?.length > 0;
        const filterByVehicleIds = this.filters?.groupFilter?.group?.VehicleIds?.length > 0;

        this.journeys = (filterByDriverIds || filterByVehicleIds) ? this.journeys.filter(journey => {
            const isRelevantDriver: boolean = (this.filters.groupFilter.group.DriverIds.indexOf(journey.DriverId) > -1);
            const isRelevantVehicle: boolean = (this.filters.groupFilter.group.VehicleIds.indexOf(journey.VehicleId) > -1);

            return isRelevantDriver || isRelevantVehicle;
        }) : this.journeys;
    }

    private prepareDefaultView(): void {
        this.vehicleJourneysMaps = this.journeyService.mapVehicleJourneys(this.journeys, this.vehicles);
        this.timeRangeActivityBars = this.generateTimeRangeActivityBars(this.journeys);

        this.refreshMapWithOptionalFit(true);
    }

    private refreshMapWithOptionalFit(fitMap: boolean = false): void {
        if (!this.veriMap?.mapInitialised) {
            return;
        }

        this.veriMap.clearData();
        this.formatJourneys(this.journeys);

        // update data containers
        let relevantJourneyPool: IFleetHistoryJourney[];
        relevantJourneyPool = (this.timeFilterIsApplied())
            ? this.journeyService.filterJourneysByTime(this.journeys, 0, this.filteredStartMinuteOfDay, this.filteredEndMinuteOfDay)
            : this.journeys;
        relevantJourneyPool = this.filterJourneysForSelectedOrHasPlotData(relevantJourneyPool);
        this.paintJourneysByVehicle(relevantJourneyPool);

        this.journeyList = [...relevantJourneyPool];

        const visibleJourneys: IFleetHistoryJourney[] = relevantJourneyPool.filter(journey => journey.visibleInBounds);
        if (visibleJourneys?.length) {
            this.totalJourneysVisibleInBounds = visibleJourneys.length;
            this.totalDriversVisibleInBounds = visibleJourneys.map(journey => journey.DriverId).filter((value, index, self) => self.indexOf(value) === index)?.length;
            this.totalVehiclesVisibleInBounds = visibleJourneys.map(journey => journey.VehicleId).filter((value, index, self) => self.indexOf(value) === index)?.length;
        } else {
            this.totalJourneysVisibleInBounds = 0;
            this.totalDriversVisibleInBounds = 0;
            this.totalVehiclesVisibleInBounds = 0;
        }

        this.journeyMap = visibleJourneys;

        if (this.inHeatmapMode) {
            this.createHeatmap();
        } else {
            this.buildPolylines(this.journeyMap);
        }

        let alreadyFit: boolean = false;
        if (fitMap) {
            alreadyFit = true;
            this.veriMap.fitMap(this.journeyService.getLatLngsForJourneys(this.journeyList));
        }

        if ((!alreadyFit && this.selectedJourney) && (fitMap || this.mapZoomLevel < this.vehicleMarkerMinZoomLevel)) {
            // parked for future
            // this.veriMap.fitMap(this.journeyService.getLatLngsForJourneys([this.selectedJourney]));
        }

        this.$timeout(() => {
            this.$scope.$apply();
        }, 100);
    }

    private createExportDataFromJourneyMap(): void {
        this.exportData = [];
        if (!this.journeyMap) {
            return;
        }

        this.journeyMap.filter(journey => journey.plots).forEach(journey => {
            this.exportData.push(
                {
                    cells: [
                        { contents: `${this.veriDate.formatDate(journey.StartUtc, "date")} (${this.veriDate.formatDate(journey.StartUtc, "time")} - ${this.veriDate.formatDate(journey.EndUtc, "time")})` },
                        { contents: journey.vehicle.Reg },
                        { contents: journey.driver?.Name }
                    ],
                    children: this.createExportDataFromJourneyPlots(journey)
                });
        });
    }

    private createExportDataFromJourneyPlots(journey: IFleetHistoryJourney): IVeriTableRow<any>[] {
        var journeyExportData: IVeriTableRow<any>[] = [];
        if (!journey.plots) {
            return journeyExportData;
        }

        journey.plots.forEach(plot => {
            journeyExportData.push({
                cells: [
                    { contents: "" },
                    { contents: this.veriDate.formatDate(plot.Utc) },
                    { contents: plot.Velocity.toString() },
                    { contents: this.plotService.convertDirectionToWords(plot.Direction) },
                    { contents: ReasonCode[plot.Reason] },
                    { contents: plot.Odometer.toString() },
                    { contents: plot.LocationInformation?.LocationDescription || `${plot.Latitude} - ${plot.Longitude}` }
                ]
            });
        });

        return journeyExportData;
    }

    private formatJourneys(journeys: IFleetHistoryJourney[]): void {
        this.journeyService.checkIfJourneysInMapBounds(this.veriMap, journeys, this.filteredStartMinuteOfDay, this.filteredEndMinuteOfDay);
        this.journeyService.sortJourneysByStartTime(journeys);
        this.journeyService.sortJourneysByInMapBounds(journeys);
    }

    private generateTimeRangeActivityBars(journeys: IFleetHistoryJourney[] = []): ITimeRangeActivityBar[] {
        if (journeys?.length <= 0) {
            return;
        }

        const timeRangeActivityBars: ITimeRangeActivityBar[] = [];
        let maxTotal: number = 0;

        for (var i = 0; i < 24; i++) {

            const existingMetric = timeRangeActivityBars[i];
            if (!existingMetric) {
                timeRangeActivityBars.push({
                    hour: i,
                    total: 0,
                    maxTotal,
                    percentage: 0
                });
            }

            const earliestMomentForFilteredDay: moment.Moment = this.veriDate.createMoment(this.filters.dateFilter.start).set({ hour: i, minute: 0 });
            const latestMomentForFilteredDay: moment.Moment = this.veriDate.createMoment(this.filters.dateFilter.start).set({ hour: i, minute: 59 });

            journeys.forEach(journey => {

                const journeyStartTime = this.veriDate.createMoment(journey.StartUtc);
                const journeyEndTime = this.veriDate.createMoment(journey.EndUtc);

                if (journeyStartTime.isBefore(latestMomentForFilteredDay, "minute") && journeyEndTime.isAfter(earliestMomentForFilteredDay, "minute")) {
                    timeRangeActivityBars[i].total++;

                    if (timeRangeActivityBars[i].total > maxTotal) {
                        maxTotal = timeRangeActivityBars[i].total;
                    }
                }
            });

            timeRangeActivityBars.forEach(metric => {
                metric.maxTotal = maxTotal;
                metric.percentage = (metric.total / metric.maxTotal) * 100;
            });
        }

        return timeRangeActivityBars;
    }

    private utcIsWithinTimeFilter(utc: string, minuteBufferEitherSide: number = 0): boolean {
        if (!this.timeFilterIsApplied()) {
            return true;
        }

        const timeFilterMinMinutesIntoDay: number = this.filteredStartMinuteOfDay - minuteBufferEitherSide;
        const timeFilterMaxMinutesIntoDay: number = this.filteredEndMinuteOfDay + minuteBufferEitherSide;

        const utcMoment: moment.Moment = this.veriDate.createMoment(utc);
        const utcMinutesIntoTheDay: number = (utcMoment.hour() * 60) + utcMoment.minute();

        return utcMinutesIntoTheDay >= timeFilterMinMinutesIntoDay && utcMinutesIntoTheDay <= timeFilterMaxMinutesIntoDay;
    }

    private timeFilterIsApplied(): boolean {
        if (this.filters?.timeFilterFrom?.hour !== null
            && this.filters?.timeFilterFrom?.minute !== null
            && this.filters?.timeFilterTo?.hour !== null
            && this.filters?.timeFilterTo?.minute !== null) {
            return true;
        }

        return false;
    }

    private filterJourneysForSelectedOrHasPlotData(journeys: IFleetHistoryJourney[]): IFleetHistoryJourney[] {
        if (!journeys) {
            return [];
        }

        return journeys.filter(journey => {
            const hasRelevantJourneyData = journey.plots || journey.LinePoints;
            return (this.selectedJourney) ? journey === this.selectedJourney : hasRelevantJourneyData;
        });
    }

    private paintJourneysByVehicle(journeys: IFleetHistoryJourney[]): void {
        if (!journeys?.length) {
            return;
        }

        const assignedVehicleColors: any[] = [];

        journeys.forEach(journey => {
            const existingColorForThisVehicle: string = assignedVehicleColors[journey?.VehicleId];

            if (existingColorForThisVehicle) {
                journey.lineColor = existingColorForThisVehicle;

                if (journey.vehicle) {
                    journey.vehicle.journeyColor = existingColorForThisVehicle;
                }
            } else {
                const newColor = this.colourService.getVigetColor(journey?.VehicleId);
                assignedVehicleColors[journey?.VehicleId] = newColor;
                journey.lineColor = newColor;

                if (journey.vehicle) {
                    journey.vehicle.journeyColor = newColor;
                }
            }
        });
    }

    private getJourneyFromJourneyId(id: number, journeys: IFleetHistoryJourney[]): IFleetHistoryJourney {
        if (!journeys) {
            return null;
        }

        return journeys.find(journey => journey.Id === id);
    }

    private buildPolylines(journeys: IFleetHistoryJourney[]) {
        if (!journeys?.length) {
            this.journeyPolylines = [];
            return;
        }

        const totalPlotPromisesRequired = this.getTotalJourneyPlotPromisesForView(journeys);
        const journeyPlotPromises = this.createJourneyPlotPromises(journeys, totalPlotPromisesRequired, this.MAX_PLOT_PROMISE_COUNT);

        if (journeyPlotPromises.length > 0) {
            this.$q.all(journeyPlotPromises).then(() => {
                this.createPolylinesFromJourneys(journeys);
                this.createExportDataFromJourneyMap();
            });
        } else {
            this.createPolylinesFromJourneys(journeys);
            this.createExportDataFromJourneyMap();
        }
    }

    private createJourneyPlotPromises(journeys: IFleetHistoryJourney[], totalRequired: number, maxCount: number): angular.IPromise<any>[] {
        const promises: angular.IPromise<any>[] = [];

        if (totalRequired <= maxCount && totalRequired > 0) {
            journeys.forEach(journey => {
                if ((!journey?.plots || journey?.plots?.length <= 0) && promises.length < maxCount) {
                    promises.push(this.getPlotsForJourneyAndVehicle(journey));
                }
            });
        }

        return promises;
    }

    private createPolylinesFromJourneys(journeys: IFleetHistoryJourney[]): void {
        let createdPolylines: IPolyline[] = [];

        journeys.forEach(journey => {
            const events = {
                mouseover: (p: google.maps.Polyline, eventName: string, model: any, args: any[]) => {
                    //var mouseEvent: google.maps.MouseEvent = args[0];
                }
            }

            let currentJourneyPolylines: IPolyline[] = [];

            const journeyHasPlots: boolean = this.journeyHasPlots(journey);
            const showDirectionArrows: boolean = journeyHasPlots && this.mapZoomLevel >= this.vehicleMarkerMinZoomLevel;
            const lineAndArrowColor = journey.lineColor;

            if (journeyHasPlots) {
                currentJourneyPolylines = this.createSegmentedPolylineForJourney(journey, events);
            } else {
                currentJourneyPolylines = [this.journeyService.createJourneyPolyline(journey, events, false)];
            }

            if (showDirectionArrows) {
                this.addDirectionArrowsToPolylines(currentJourneyPolylines, lineAndArrowColor);
            } else {
                this.removeDirectionArrowsFromPolylines(currentJourneyPolylines);
            }

            if (currentJourneyPolylines) {
                currentJourneyPolylines.forEach(newPolyline => {
                    newPolyline.data = {
                        startMinuteOfDay: journey.startMinuteOfDay,
                        endMinuteOfDay: journey.endMinuteOfDay
                    }

                    createdPolylines.push(newPolyline);
                });
            }
        });

        this.journeyPolylines = createdPolylines;
    }

    private addDirectionArrowsToPolylines(polylines: IPolyline[], color: string = `white`): void {
        if (polylines?.length) {
            polylines.forEach(polyline => {
                if (!polyline?.options?.icons) {
                    polyline.options.icons = [];
                }

                polyline.options.icons.push({
                    icon: {
                        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        fillOpacity: 1,
                        scale: 3,
                        fillColor: "white",
                        strokeColor: color,
                        strokeWeight: 1,
                    },
                    offset: "100%",
                    repeat: "100px"
                });
            });
        }
    }

    private removeDirectionArrowsFromPolylines(polylines: IPolyline[]): void {
        if (polylines?.length) {
            polylines.forEach(polyline => {
                polyline.options.icons = [];
            });
        }
    }

    // needs refactor / fresh look
    private createSegmentedPolylineForJourney(journey: IFleetHistoryJourney, journeyEvents?: any): IPolyline[] {
        const createdSegmentedPolylines: IPolyline[] = [];
        const plotGroups: IFleetHistoryPlotGroup[] = [];

        // loop though plots in journey and add them the relevancy groups
        journey.plots.forEach(plot => {
            const plotTimeWithinFilter: boolean = this.utcIsWithinTimeFilter(plot.Utc);

            const mostRecentPlotGroup: IFleetHistoryPlotGroup = plotGroups[plotGroups.length - 1];
            if (mostRecentPlotGroup) {
                let mostRecentTimeFilter: boolean = mostRecentPlotGroup.inTimeFilter;

                if (mostRecentTimeFilter === plotTimeWithinFilter) {
                    this.addPlotToLatestPlotGroup(plot, plotGroups, plotTimeWithinFilter);
                } else {
                    this.addPlotToNewPlotGroup(plot, plotGroups, plotTimeWithinFilter);
                }
            } else {
                this.addPlotToNewPlotGroup(plot, plotGroups, plotTimeWithinFilter);
            }
        });

        // loop through relevancy groups and create polylines for group
        plotGroups.forEach(plotGroup => {

            // plot group has plots
            if (plotGroup?.plots.length > 0) {
                let newPolyline: IPolyline;

                // create a polyline for this plot group
                if (plotGroup.inTimeFilter) {
                    newPolyline = this.createPolyline(journey, plotGroup.plots, true, journeyEvents);
                } else {
                    newPolyline = this.createPolyline(journey, plotGroup.plots, false, journeyEvents);
                }

                // add the new polyline to the overall segments array
                if (newPolyline) {
                    createdSegmentedPolylines.push(newPolyline);
                }
            }
        });

        createdSegmentedPolylines.forEach((polyline, index) => {
            polyline.id = `${polyline.id}-${index}`;
        });

        return createdSegmentedPolylines;
    }

    private addPlotToLatestPlotGroup(plot: IPlot, existingPlotGroups: IFleetHistoryPlotGroup[], inTimeFilter: boolean): void {
        if (!plot || !existingPlotGroups) {
            return;
        }

        // check if any groups exist yet
        if (existingPlotGroups.length === 0) {
            // add new group and add plot to it
            this.addPlotToNewPlotGroup(plot, existingPlotGroups, inTimeFilter);
        } else {
            // add plot to latest existing group
            const latestPlotGroup = existingPlotGroups[existingPlotGroups.length - 1];
            latestPlotGroup.inTimeFilter = inTimeFilter;
            latestPlotGroup.plots.push(plot);
        }
    }

    private addPlotToNewPlotGroup(plot: IPlot, existingPlotGroups: IFleetHistoryPlotGroup[], inTimeFilter: boolean): void {
        if (!plot || !existingPlotGroups) {
            return;
        }

        // add a final plot point to any previous groups, to stitch the two groups together (otherwise there will be a gap)
        const mostRecentExistingPlotGroup = existingPlotGroups[existingPlotGroups?.length - 1];
        const mostRecentPlotGroupInTime: boolean = (mostRecentExistingPlotGroup) ? mostRecentExistingPlotGroup.inTimeFilter : false;
        let initialDashedFromSolid: IPlot;

        if (mostRecentExistingPlotGroup) {
            if (!mostRecentPlotGroupInTime) {
                // add a final dashed polyline to stitch to start of solid polyline
                mostRecentExistingPlotGroup.plots.push(plot);
            } else {
                // add initial dashed polyline to stitch from end of solid line to start of dashed polyline
                initialDashedFromSolid = mostRecentExistingPlotGroup?.plots?.[mostRecentExistingPlotGroup?.plots?.length - 1];
            }
        }

        // add new group with this plot
        existingPlotGroups.push({
            inTimeFilter: inTimeFilter,
            plots: (initialDashedFromSolid) ? [initialDashedFromSolid, plot] : [plot]
        });
    }

    private resetFilters(): void {
        this.filters.timeFilterFrom = {
            hour: 0,
            minute: 0
        };

        this.filters.timeFilterTo = {
            hour: 23,
            minute: 59
        };

        this.updateStartMinuteOfDay(this.filters.timeFilterFrom);
        this.updateEndMinuteOfDay(this.filters.timeFilterTo);

        this.onDateFilterChanged(this.veriDate.dateRangeFromEnum(this.filters.defaultDateRange));
    }

    private createPolyline(journey: IFleetHistoryJourney, plots: IPlot[], relevant: boolean, journeyEvents: any = null): IPolyline {
        let latLngArray: google.maps.LatLng[];
        let newPolyline: IPolyline;
        if (plots?.length > 0) {
            latLngArray = this.mapService.convertPlotsToGoogleLatLng(plots);
            newPolyline = this.mapService.createPolyline(journey.Id, latLngArray, journey.lineColor, journeyEvents, !relevant);

            if (relevant) {
                newPolyline.options.stroke.weight = 3;
                newPolyline.options.strokeOpacity = .9;
                newPolyline.options.icons = null;

            } else {
                newPolyline.options.strokeOpacity = .5;
                newPolyline.options.icons[0].icon.strokeOpacity = .5;
            }
        }

        return newPolyline;
    }

    private getTotalJourneyPlotPromisesForView(journeys: IFleetHistoryJourney[]): number {
        let totalRequiredPromises: number = 0;

        if (!journeys?.length) {
            return totalRequiredPromises;
        }

        let correctJourneys = journeys.filter(journey => journey.visibleInBounds);

        if (!correctJourneys?.length) {
            return totalRequiredPromises;
        }

        correctJourneys.forEach(journey => {
            if (!this.journeyHasPlots(journey)) {
                totalRequiredPromises++;
            }
        });

        return totalRequiredPromises;
    }

    private journeyHasPlots(journey: IFleetHistoryJourney): boolean {
        return journey?.plots?.length > 0;
    }

    private updateStartMinuteOfDay(timeFilter: IFleetHistoryTimeFilter): void {
        this.filteredStartMinuteOfDay = (timeFilter.hour * 60) + timeFilter.minute;
    }

    private updateEndMinuteOfDay(timeFilter: IFleetHistoryTimeFilter): void {
        this.filteredEndMinuteOfDay = (timeFilter.hour * 60) + timeFilter.minute;
    }

    private createSelectedJourneyMarkers(journey: IFleetHistoryJourney): void {
        if (!journey) {
            return;
        }

        this.selectedJourneyMapMarkerStart = this.createMapMarkerForJourney(
            journey,
            journey?.StartLatitude,
            journey?.StartLongitude,
            "Start",
            "play_arrow");

        this.selectedJourneyMapMarkerEnd = this.createMapMarkerForJourney(
            journey,
            journey?.EndLatitude,
            journey?.EndLongitude,
            "End",
            "flag");
    }

    private createClosestPlotmarkersForJourneys(journeys: IFleetHistoryJourney[], closestToTime: moment.Moment = null): void { // use closest to mouse if not time
        if (!this.filters?.dateFilter?.start) {
            return;
        }

        if (closestToTime) {
            closestToTime = this.veriDate.createMoment(this.filters.dateFilter.start).set({ hour: closestToTime.hour(), minute: closestToTime.minute() });
        }

        if (this.mapZoomLevel < this.vehicleMarkerMinZoomLevel) {
            return;
        }

        if (this.creatingClosestPlotMarkers) {
            return;
        }

        this.creatingClosestPlotMarkers = true;

        this.vehiclesDisplay = [];
        this.mouseMoveJourneyPlotMarkers = [];
        const mapBounds: google.maps.LatLngBounds = this.veriMap?.getMap()?.getBounds();

        if (!mapBounds) {
            this.creatingClosestPlotMarkers = false;
            return;
        }

        journeys.forEach(journey => {
            if (this.journeyHasPlots(journey) && (closestToTime || this.mouseLatLngs)) {

                const timeRelevantPlots: IPlot[] = journey.plots.filter(plot => this.utcIsWithinTimeFilter(plot.Utc));

                const closestPlot: IPlot = (closestToTime)
                    ? this.plotService.findPlotClosestToTime(closestToTime, timeRelevantPlots)
                    : this.plotService.findPlotClosestToLatLng(timeRelevantPlots, this.mouseLatLngs);

                if (closestPlot) {
                    const existingDisplayVehicle: IFleetHistoryVehicle = this.vehiclesDisplay.find(vehicle => vehicle.Id === journey?.VehicleId);

                    if (existingDisplayVehicle) {
                        const cachedPlot: IPlot = existingDisplayVehicle.Plot;
                        const finalPlot: IPlot = (closestToTime)
                            ? this.plotService.findPlotClosestToTime(closestToTime, [cachedPlot, closestPlot])
                            : this.plotService.findPlotClosestToLatLng([cachedPlot, closestPlot], this.mouseLatLngs);

                        existingDisplayVehicle.Plot = finalPlot;
                    } else {
                        journey.vehicle.Plot = closestPlot;
                        this.vehiclesDisplay.push(journey.vehicle);
                    }
                }
            }
        });

        this.$timeout(() => {
            this.$scope.$apply();
            this.creatingClosestPlotMarkers = false;
        }, 10);
    }

    private createMapMarkerForJourney(journey: IFleetHistoryJourney, latitude: number, longitude: number, label: string, icon: string): IGoogleMapsMarker<IJourney> { //new
        if (!journey || !latitude || !longitude || !label || !icon) {
            return;
        }

        const markerOptions: any = this.googleMarkerService.createEventMarkerOptions({
            label: label,
            subIcon: icon,
            showLabel: true,
            anchorPosition: MarkerAnchorPosition.Top,
            colour: journey.lineColor,
            position: { lat: latitude,lng: longitude }
        });

        const marker: IGoogleMapsMarker<IJourney> = {
            id: journey.Id,
            options: markerOptions,
            model: journey
        };

        return marker;
    }

    private createHeatmap() {

        // Only create these when necessary
        if (this.heatMapPolygons.length) {
            return
        }

        var h3Map = this.createH3Map();
        var maxCount = 1;

        h3Map.forEach((value, key) => {
            maxCount = Math.max(value, maxCount);
        });

        var densityMap = this.h3Service.createH3DensityMap(h3Map, maxCount / 10);

        densityMap.forEach(d => {
            var megaPolygon = h3.h3SetToMultiPolygon(d.h3array, true);

            var data = {
                type: "FeatureCollection",
                features: [{
                    type: "Feature",
                    properties: {
                        fillColor: this.h3Service.getHeatmapColor(d.density)
                    },
                    geometry: {
                        type: "MultiPolygon",
                        coordinates: megaPolygon
                    }
                }]
            };

            this.veriMap.getMap().data.addGeoJson(data);
            this.veriMap.getMap().data.setStyle((feature) => {
                return {
                    fillColor: feature.getProperty('fillColor'),
                    strokeWeight: 0,
                    //strokeColor: this.getHeatmapColor(1),
                    fillOpacity: 1
                };
            });
        });
    }

    private createH3Map(): Map<string, number> {
        var h3Map = new Map<string, number>();
        var h3Resolution = 7;

        for (var jIndex = 0; jIndex < this.journeyMap.length; jIndex++) {
            const journey = this.journeys[jIndex];

            if (!journey.LinePoints) {
                //console.warn("No line points available for journey", journey);
                continue;
            }

            var previousHexCoords = h3.geoToH3(journey.LinePoints[0][0], journey.LinePoints[0][1], h3Resolution);
            for (var i = 1; i < journey.LinePoints.length; i++) {
                const point = journey.LinePoints[i];

                var hexCoords = h3.geoToH3(point[0], point[1], h3Resolution);
                var hexLine = h3.h3Line(previousHexCoords, hexCoords);

                for (var hIndex = 0; hIndex < hexLine.length; hIndex++) {
                    const lineHex = hexLine[hIndex];

                    if (h3Map.has(lineHex)) {
                        var newCount = h3Map.get(lineHex) + 1;
                        h3Map.set(lineHex, newCount);
                    }
                    else {
                        h3Map.set(lineHex, 1);
                    }
                }

                previousHexCoords = hexCoords;
            }
        }

        return h3Map;
    }
}

export class FleetHistoryComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            resourceFilter: "<"
        };

        this.controller = ComponentController;
        this.template = require("./fleetHistory.component.html");
    }
}
